import { configureStore } from "@reduxjs/toolkit";
import { persistStore } from "redux-persist";
import { createEpicMiddleware } from "redux-observable";
import {
  configureInjectableEpicMiddleware,
  runCoreEpicsAfterStoreCreated,
} from "@/store/epics/injectableEpicRuntime";
import type { EpicDependencies } from "@/store/epics/epicDependencies";
import { rootReducer } from "@/store/app/rootReducer";
import { clearActiveSparxOnSelectionChangeMiddleware } from "@/store/middleware/clearActiveSparxOnSelectionChangeMiddleware";
import { sparxsEditorBlueprintHydrateGuardMiddleware } from "@/store/middleware/sparxsEditorBlueprintHydrateGuardMiddleware";
import type { RootState } from "@/store/app/storeTypes";
import type { PublicRuntimeConfig } from "@/lib/config/publicRuntimeConfig";
import { initOpenApiClientRuntime } from "@/store/consoleApi/consoleOpenApiConfiguration";
import {
  setAppStoreInstance,
  setPersistorInstance,
} from "@/store/app/appStoreInstance";

const assertPositiveIntegerStatisticIntervalSeconds = (
  seconds: unknown,
): number => {
  if (
    typeof seconds !== "number" ||
    !Number.isInteger(seconds) ||
    seconds <= 0
  ) {
    throw new Error(
      `statisticFetchingIntervalSeconds must be a positive integer (received ${String(seconds)})`,
    );
  }
  return seconds;
};

export const createAppStore = ({
  statisticFetchingIntervalSeconds,
  consoleServiceOrigin,
}: PublicRuntimeConfig) => {
  initOpenApiClientRuntime(consoleServiceOrigin);

  const statisticPollingIntervalMs =
    assertPositiveIntegerStatisticIntervalSeconds(
      statisticFetchingIntervalSeconds,
    ) * 1000;

  const epicDependencies: EpicDependencies = {
    statisticPollingIntervalMs,
  };

  const epicMiddleware = createEpicMiddleware<
    unknown,
    unknown,
    RootState,
    EpicDependencies
  >({
    dependencies: epicDependencies,
  });

  configureInjectableEpicMiddleware(epicMiddleware);

  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: {
          warnAfter: 128,
        },
        serializableCheck: {
          ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
        },
      }).concat(
        clearActiveSparxOnSelectionChangeMiddleware,
        sparxsEditorBlueprintHydrateGuardMiddleware,
        epicMiddleware,
      ),
  });

  runCoreEpicsAfterStoreCreated();

  const persistor = persistStore(store);
  setAppStoreInstance(store);
  setPersistorInstance(persistor);

  return { store, persistor };
};
